import { PricebookItem } from '../../admin/components/tools/PricingWizard/types';

/**
 * Get the unit price for a component by name search
 */
export function getComponentPrice(pricebookItem: PricebookItem, searchTerm: string): number {
	const component = pricebookItem.chargifyOfferComponents?.find(c =>
		c.name?.toLowerCase().includes(searchTerm.toLowerCase())
	);
	return component?.unitPrice ?? 0;
}

export interface PricingBreakdown {
	// Base pricing
	basePriceMRR: number;
	basePrice: number;

	// Extra users
	extraUserCostMRR: number;
	extraUserCost: number;

	// Add-ons
	facebookAdsMRR: number;
	facebookAdsFee: number;
	googleLSAMRR: number;
	googleLSAFee: number;
	handwrittenCardsMRR: number;
	handwrittenCardsFee: number;

	// Totals
	onboardingFee: number;
	subtotal: number;
	discountAmount: number;
	discountPercentage: number;
}

/**
 * Calculate all pricing values from a pricebook item
 */
export function calculatePricingBreakdown(pricebookItem: PricebookItem): PricingBreakdown {
	const billingCycle = pricebookItem.productBillingCycle;

	// Base price
	const basePriceMRR = pricebookItem.productMRR;
	const basePrice = basePriceMRR * 12;

	// Extra users
	const extraUserCostMRR =
		pricebookItem.additionalUsers > 0
			? (getComponentPrice(pricebookItem, 'additional user') / billingCycle) * pricebookItem.additionalUsers
			: 0;
	const extraUserCost = extraUserCostMRR * 12;

	// Add-ons
	const facebookAdsMRR = pricebookItem.hasFacebookAdsPremium
		? (getComponentPrice(pricebookItem, 'meta ads premium') ||
				getComponentPrice(pricebookItem, 'facebook ads premium') ||
				getComponentPrice(pricebookItem, 'facebook premium')) / billingCycle
		: 0;
	const facebookAdsFee = facebookAdsMRR * 12;

	const googleLSAMRR = pricebookItem.hasGoogleLocalServiceAds
		? getComponentPrice(pricebookItem, 'google lsa') / billingCycle
		: 0;
	const googleLSAFee = googleLSAMRR * 12;

	const handwrittenCardsMRR = pricebookItem.hasHandwrittenCards
		? getComponentPrice(pricebookItem, 'handwritten') / billingCycle
		: 0;
	const handwrittenCardsFee = handwrittenCardsMRR * 12;

	// Totals
	const onboardingFee = pricebookItem.implementationPrice;
	const subtotal = pricebookItem.totalFirstYearCostBeforeDiscount;
	const discountAmount = pricebookItem.totalFirstYearCostBeforeDiscount - pricebookItem.totalFirstYearCostAfterDiscount;
	const discountPercentage = pricebookItem.discount * 100;

	return {
		basePriceMRR,
		basePrice,
		extraUserCostMRR,
		extraUserCost,
		facebookAdsMRR,
		facebookAdsFee,
		googleLSAMRR,
		googleLSAFee,
		handwrittenCardsMRR,
		handwrittenCardsFee,
		onboardingFee,
		subtotal,
		discountAmount,
		discountPercentage,
	};
}
